import React from "react";
import { motion } from "framer-motion";
import { Crosshair, Lock } from "lucide-react";

const GOLD = "#D4AF37";
const ORDER = ["ball_control", "dribbling", "shooting", "ball_mastery"];

const Bar = ({ label, pct, delay }) => (
  <div className="min-w-0">
    <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-[0.18em] text-white/50"><span className="truncate">{label}</span><span style={{ color: GOLD }}>{pct == null ? "—" : `${pct}%`}</span></div>
    <div className="h-1 rounded-full bg-white/10 mt-1 overflow-hidden">
      <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, #C41E24, ${GOLD})` }} initial={{ width: 0 }} animate={{ width: `${pct ?? 0}%` }} transition={{ duration: 0.9, delay, ease: "easeOut" }} />
    </div>
  </div>
);

// Core Four scouting markers read by the Engine on this clip (feed the auto-derived O-Rank ratings).
export const TechnicalMarkers = ({ proof, markers }) => {
  if (!markers) return null;
  const list = ORDER.filter((k) => markers[k]).map((k) => ({ key: k, ...markers[k] }));
  if (!list.length) return null;
  return (
    <div data-testid={`technical-markers-${proof.id}`} className="rounded-xl border border-[#D4AF37]/35 bg-black/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Crosshair size={13} style={{ color: GOLD }} />
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/70">Core Four · Technical Markers</span>
        <span className="ml-auto font-mono text-[8px] tracking-widest uppercase text-white/35">{list.filter((m) => m.observed).length}/4 read on this clip</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {list.map((m, i) => (
          <div key={m.key} data-testid={`marker-${m.key}-${proof.id}`} className={`rounded-lg border p-2.5 space-y-2 transition-opacity ${m.observed ? "border-[#D4AF37]/40" : "border-white/10 opacity-45"}`}
            style={m.observed ? { background: "linear-gradient(160deg, rgba(212,175,55,0.08), rgba(0,0,0,0.5))" } : {}}>
            <div className="flex items-start justify-between gap-1">
              <div className="font-headings text-sm uppercase tracking-wide leading-none text-white">{m.label}</div>
              <div className="font-cover text-xl leading-none" style={{ color: m.observed ? GOLD : "rgba(255,255,255,0.35)" }}>{m.observed ? m.score : "—"}<span className="text-[9px] text-white/35">/10</span></div>
            </div>
            {m.observed ? (
              <>
                <Bar label={m.primary_label} pct={m.primary} delay={0.1 * i} />
                <Bar label={m.secondary_label} pct={m.secondary} delay={0.1 * i + 0.15} />
                {m.lock && <div data-testid={`marker-lock-${m.key}-${proof.id}`} className="flex items-start gap-1.5 font-mono text-[10px] leading-snug pt-1 border-t border-white/5" style={{ color: GOLD }}><Lock size={10} className="mt-0.5 shrink-0" />{m.lock}</div>}
              </>
            ) : (
              <div className="font-mono text-[9px] uppercase tracking-wider text-white/40 leading-snug">Not shown in this clip · {m.primary_label} · {m.secondary_label}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
